<template>
  <v-layout column fill-height>
    <v-toolbar flat>
      <v-toolbar-title>
        {{ $t('customer.text.title') }}
      </v-toolbar-title>
      <v-spacer />
      <CSVExport
        v-if="$store.getters.superuserLoggedIn"
        :total-count="totalCount"
        :fetch="() => $api.customers().list(fetchParams)"
        :headers="csvHeaders"
        :disabled="questionLoading"
        filename-prefix="customer-"
      />
      <v-divider class="mx-2" vertical />
      <v-btn color="primary" to="/customer/create">
        {{ $t('customer.text.newRegistration') }}
      </v-btn>
    </v-toolbar>
    <v-row>
      <v-col xs="12" sm="3">
        <v-text-field
          v-model="filter.search"
          outlined
          :label="$t('customer.text.search')"
          :placeholder="$t('customer.text.searchPlaceholder')"
          hide-details
          append-icon="search"
          clearable
          @click:clear="clearSearch"
          @click:append="updateQuery"
          @keypress.enter="updateQuery"
        />
      </v-col>
      <v-col xs="6" sm="3" md="2">
        <ShopAutocomplete
          v-model="filter.shop"
          outlined
          :label="$t('customer.text.shop')"
          hide-details
          clearable
          @change="updateQuery"
        />
      </v-col>

      <v-col xs="6" sm="3" md="2">
        <DateSelect
          v-model="filter.registeredFrom"
          outlined
          :label="$t('customer.text.registeredDateFrom')"
          clearable
          hide-details
          @input="updateQuery"
        />
      </v-col>
      <v-col xs="6" sm="3" md="2">
        <DateSelect
          v-model="filter.registeredTo"
          outlined
          :label="$t('customer.text.registeredDateTo')"
          clearable
          hide-details
          @input="updateQuery"
        />
      </v-col>
      <v-col xs="6" sm="3" md="2">
        <SelectNullBoolean
          v-model="filter.hasUser"
          :label="$t('customer.text.appRegistered')"
          @input="updateQuery"
        />
      </v-col>
    </v-row>
    <virtual-data-table
      :items="customers"
      :identifier="identifier"
      :total-count="totalCount"
      :ordering.sync="filter.ordering"
      @update:ordering="updateQuery"
      @infinite="fetchData"
    >
      <virtual-data-table-column
        :label="$t('customer.text.memberNumber')"
        prop="account"
        width="100"
        fixed="left"
      >
        <template #default="props">
          <a @click="pushDetail(props)">{{ props.item.account }}</a>
        </template>
      </virtual-data-table-column>

      <virtual-data-table-column
        :label="$t('customer.text.shopName')"
        prop="shopName"
      />
      <virtual-data-table-column
        v-if="$i18n.locale === 'ja'"
        :label="$t('customer.text.familyNameKana')"
        prop="familyNameKana"
        width="100"
      />
      <virtual-data-table-column
        v-if="$i18n.locale === 'ja'"
        :label="$t('customer.text.givenNameKana')"
        prop="givenNameKana"
        width="100"
      />
      <virtual-data-table-column
        :label="$t('customer.text.fullName')"
        width="150"
      >
        <template #default="props">
          {{ props.item.familyName }}
          {{ props.item.givenName }}
        </template>
      </virtual-data-table-column>
      <virtual-data-table-column
        :label="$t('customer.text.phoneNumber')"
        prop="phoneNumber"
        width="150"
      />
      <virtual-data-table-column :label="$t('customer.text.appName')">
        <template #default="{ item: { userdata } }">
          <span v-if="userdata">
            {{ userdata.familyName }}
            {{ userdata.firstName }}
            <span v-if="$i18n.locale === 'ja'">
              ({{ userdata.familyNameKana }} {{ userdata.firstNameKana }})
            </span>
          </span>
        </template>
      </virtual-data-table-column>
      <virtual-data-table-column
        :label="$t('customer.text.appUid')"
        prop="userdata.uid"
        width="200"
      />
      <virtual-data-table-column
        :label="$t('customer.text.appEmail')"
        prop="userdata.email"
        width="300"
      />
      <virtual-data-table-column
        :label="$t('customer.text.appPhoneNumber')"
        prop="userdata.phoneNumber"
        width="150"
      />
      <virtual-data-table-column
        :label="$t('customer.text.firstVisitDate')"
        prop="aggregation.firstVisit"
        width="100"
      />
      <virtual-data-table-column
        :label="$t('customer.text.lastVisitDate')"
        prop="aggregation.lastVisit"
        width="100"
      />
      <virtual-data-table-column
        :label="$t('customer.text.visitCount')"
        prop="aggregation.visitCount"
        width="100"
      />
      <virtual-data-table-column
        :label="$t('customer.text.firstVisitRoute')"
        prop="aggregation.firstReservedBy"
        width="100"
      />
      <virtual-data-table-column
        :label="$t('customer.text.maxVisitShop')"
        prop="aggregation.maxVisitShopName"
        width="100"
      />
      <virtual-data-table-column
        :label="$t('customer.text.registeredDateTime')"
        prop="createdAt"
        width="150"
      >
        <template #default="{ item: { createdAt } }">
          {{ createdAt | datetime }}
        </template>
      </virtual-data-table-column>
      <virtual-data-table-column
        :label="$t('customer.text.updatedDateTime')"
        prop="updatedAt"
        width="150"
      >
        <template #default="{ item: { createdAt } }">
          {{ createdAt | datetime }}
        </template>
      </virtual-data-table-column>
    </virtual-data-table>
    <router-view />
  </v-layout>
</template>

<script>
import moment from 'moment'
import SelectNullBoolean from '@/components/SelectNullBoolean.vue'
import CSVExport from '@/components/CSVExport.vue'
import DateSelect from '@/components/DateSelect.vue'
import ShopAutocomplete from '@/components/ShopAutocomplete.vue'

import FilterUtil from '@/filter-util'
import { formatDateForDisplay } from '@/utils'
const filterUtil = new FilterUtil({
  search: { type: String, default: null },
  ordering: { type: String, default: '' },
  hasUser: { type: Boolean, default: null },
  registeredFrom: { type: String, default: null },
  registeredTo: { type: String, default: null },
  shop: { type: String, default: null },
})
const extractTime = value => (value ? moment(value).format('HH:mm') : '')

export default {
  name: 'CustomerView',
  components: {
    SelectNullBoolean,
    CSVExport,
    DateSelect,
    ShopAutocomplete,
  },
  data: () => ({
    filter: {
      search: null,
      ordering: '',
      hasUser: null,
      registeredFrom: null,
      registeredTo: null,
      shop: null,
    },
    query: null,
    customers: [],
    next: null,
    totalCount: 0,

    questionLoading: false,
    counselingQuestions: [],
  }),
  computed: {
    identifier() {
      return JSON.stringify(this.query)
    },
    csvHeaders() {
      const q1 = this.counselingQuestions.find(
        x => x.shortText === 'お店を知ったきっかけ' && x.version === '2'
      )
      const q13 = this.counselingQuestions.find(
        x => x.shortText === 'お店を知ったきっかけ' && x.version === '3'
      )
      let q2 = this.counselingQuestions.find(x => x.shortText === '感じた良さ')
      const baseHeaders = [
        { key: 'uuid', label: String(this.$t('customer.csv.id')) },
        { key: 'shopCode', label: String(this.$t('customer.csv.shopCode')) },
        { key: 'shopName', label: String(this.$t('customer.csv.shopName')) },
        {
          key: 'familyName',
          label: String(this.$t('customer.csv.familyName')),
        },
        { key: 'givenName', label: String(this.$t('customer.csv.givenName')) },
        ...(this.$i18n.locale === 'ja'
          ? [
              {
                key: 'familyNameKana',
                label: String(this.$t('customer.csv.familyNameKana')),
              },
              {
                key: 'givenNameKana',
                label: String(this.$t('customer.csv.givenNameKana')),
              },
            ]
          : []),
        { key: 'account', label: String(this.$t('customer.csv.account')) },
        {
          key: 'userdata.email',
          label: String(this.$t('customer.csv.appUser')),
        },
        { key: 'email', label: String(this.$t('customer.csv.email')) },
        { key: 'age', label: String(this.$t('customer.csv.age')) },
        { key: 'gender', label: String(this.$t('customer.csv.gender')) },
        {
          key: 'phoneNumber',
          label: String(this.$t('customer.csv.phoneNumber')),
        },
        { key: 'postcode', label: String(this.$t('customer.csv.postcode')) },
        {
          key: 'prefecture',
          label: String(this.$t('customer.csv.prefecture')),
        },
        { key: 'city', label: String(this.$t('customer.csv.city')) },
        { key: 'address', label: String(this.$t('customer.csv.address')) },
        { key: 'building', label: String(this.$t('customer.csv.building')) },
        { key: 'note', label: String(this.$t('customer.csv.customerMemo')) },
        {
          key: 'aggregation.maxVisitShopName',
          label: String(this.$t('customer.csv.maxVisitShop')),
        },
        {
          key: 'aggregation.firstVisit',
          label: String(this.$t('customer.csv.firstVisitDate')),
        },
        {
          key: 'aggregation.lastVisit',
          label: String(this.$t('customer.csv.lastVisitDate')),
        },
        {
          key: 'aggregation.visitCount',
          label: String(this.$t('customer.csv.visitCount')),
        },
        {
          key: 'aggregation.firstReservedBy',
          label: String(this.$t('customer.csv.firstVisitRoute')),
        },
        {
          key: item =>
            item.receivesInfo
              ? String(this.$t('customer.csv.receive'))
              : String(this.$t('customer.csv.notReceive')),
          label: String(this.$t('customer.csv.receiveNotification')),
        },
        {
          key: item => formatDateForDisplay(item.createdAt, this.$i18n.locale),
          label: String(this.$t('customer.csv.createdDate')),
        },
        {
          key: item => extractTime(item.createdAt),
          label: String(this.$t('customer.csv.createdTime')),
        },
        {
          key: item => Boolean(item.starterPassFrom),
          label: String(this.$t('customer.csv.starterPassPurchase')),
        },
        {
          key: item => item.starterPassFrom,
          label: String(this.$t('customer.csv.starterPassPurchaseDate')),
        },
        {
          key: item => item.birthday,
          label: String(this.$t('customer.csv.birthday')),
        },
        {
          key: item => formatDateForDisplay(item.updatedAt, this.$i18n.locale),
          label: String(this.$t('customer.csv.updatedDate')),
        },
        {
          key: item => extractTime(item.updatedAt),
          label: String(this.$t('customer.csv.updatedTime')),
        },
        {
          key: 'aggregation.firstVisitShopCode',
          label: String(this.$t('customer.csv.firstVisitShopCode')),
        },
        {
          key: 'aggregation.firstVisitShopName',
          label: String(this.$t('customer.csv.firstVisitShopName')),
        },
      ]
      let old = [
        {
          key: item => {
            let answers = item.counselingAnswers
            if (answers.length === 0) {
              return item.knownReasonTexts.join(',')
            }
            let id2opt = {}
            q1?.options.forEach(o => {
              id2opt[o.id] = o
            })
            q13?.options.forEach(o => {
              id2opt[o.id] = o
            })
            return answers
              .filter(x => x.question === q1?.id || x.question == q13?.id)
              .map(ans => {
                let opt = id2opt[ans.option]
                if (!opt) {
                  console.error(opt, ans.option, id2opt)
                  return ''
                }
                return ans.text ? `${opt.text}: ${ans.text}` : opt.text
              })
              .join(', ')
          },
          label: String(this.$t('customer.csv.knownReason')),
        },
        {
          key: 'visitReasonOther',
          label: String(this.$t('customer.csv.knownReasonOther')),
        },
        {
          key: item => {
            let answers = item.counselingAnswers
            if (answers.length === 0) {
              return item.visitReasonTexts.join(',')
            }
            let id2opt = {}
            q2?.options.forEach(o => {
              id2opt[o.id] = o
            })
            return answers
              .filter(x => x.question === q2?.id)
              .map(ans => {
                let opt = id2opt[ans.option]
                return ans.text ? `${opt.text}: ${ans.text}` : opt.text
              })
              .join(', ')
          },
          label: String(this.$t('customer.csv.visitReason')),
        },
        {
          key: 'knownReasonOther',
          label: String(this.$t('customer.csv.visitReasonOther')),
        },
      ]
      return baseHeaders.concat(old)
    },
    fetchParams() {
      let params = Object.assign(this.query)
      if (params.registeredFrom) {
        let m = moment(params.registeredFrom)
        if (m.isValid()) params.registeredFrom = m.toISOString()
        else delete params.registeredFrom
      }
      if (params.registeredTo) {
        let m = moment(params.registeredTo)
        if (m.isValid()) {
          m.add(1, 'days')
          params.registeredTo = m.toISOString()
        } else delete params.registeredTo
      }
      return params
    },
  },
  watch: {
    '$route.query'(query) {
      this.query = filterUtil.parseQueryDict(query)
    },
    identifier() {
      this.customers = []
      this.next = null
      this.getTotalCount()
    },
  },
  created() {
    this.query = filterUtil.parseQueryDict(this.$route.query)
    Object.assign(this.filter, this.query)
    this.getTotalCount()
    this.fetchQuestions()
  },
  methods: {
    async fetchQuestions(url) {
      this.questionLoading = true
      let { results, next } = await (url
        ? this.$api.http.get(url)
        : this.$apiV3.counselingQuestions().list({ all: true }))
      this.counselingQuestions = this.counselingQuestions.concat(results)
      if (next) this.fetchQuestions(next)
      else this.questionLoading = false
    },
    async getTotalCount() {
      this.totalCount = await this.$api.customers().count(this.fetchParams)
    },
    async fetchData($state) {
      try {
        let { results, next } = await (this.next
          ? this.$api.http.get(this.next)
          : this.$api.customers().list(this.fetchParams))
        this.customers = this.customers.concat(results)
        this.next = next
        if (this.customers.length) $state.loaded()
        if (!this.next) $state.complete()
      } catch (err) {
        $state.error(err)
      }
    },
    updateQuery() {
      let query = filterUtil.mergeQueryDict(this.query, this.filter)
      this.$router.push({ query })
    },
    clearSearch() {
      this.filter.search = ''
      this.updateQuery()
    },
    pushDetail(row) {
      this.$router.push({
        name: 'customer-detail',
        params: { id: row.item.uuid },
        query: this.$route.query,
      })
    },
  },

  metaInfo() {
    return { title: String(this.$t('customer.text.title')) }
  },
}
</script>

<style></style>
